import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { Download, X } from "lucide-react";
import { csvEscape } from "@/lib/utils/daylight-insight/records";
import { useMetricDrillDown } from "@/components/daylight-insight/api/useMetricDrillDown";
import type { MetricDrillDownParams, MetricDrillDownRow } from "@/components/daylight-insight/api/types";

/**
 * Loading state: one centred spinner with a line saying what is being fetched.
 *
 * A skeleton table used to stand in here, but it was a poor fit for THIS view —
 * a skeleton promises the shape of what's coming, and the drill-down's columns
 * are whatever the SP returns, so the placeholder's guessed 5 columns rarely
 * matched and the "no layout shift" it was meant to buy never materialised. A
 * plain spinner makes no promise it can't keep, and these record sets are the
 * slowest call in the UI — worth naming the metric while the user waits.
 */
function DrillDownLoading({ metricName }: { metricName: string }) {
  return (
    <div className="rd-loading" role="status" aria-live="polite">
      <div className="rd-spinner" />
      <div className="rd-loading-title">Loading records…</div>
      <div className="rd-loading-sub">
        Fetching the records behind {metricName}. Larger periods can take a few
        moments.
      </div>
    </div>
  );
}

/** `Hide_`-prefixed columns are the SP's internal/support fields (ids, join keys)
 *  — they come back in the payload but are never shown or exported. */
function isHiddenColumn(col: string): boolean {
  return col.toLowerCase().startsWith("hide_");
}

/** Every visible column present across the returned rows, in first-seen order.
 *  Driven entirely by the response — the SP's column set is the single source of
 *  truth, so a new column appears with no change here. */
function columnsOf(rows: MetricDrillDownRow[]): string[] {
  const cols: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (isHiddenColumn(k) || seen.has(k)) continue;
      seen.add(k);
      cols.push(k);
    }
  }
  return cols;
}

/** An ISO date the SP serialised out of a SQL `date`/`datetime` column, with an
 *  optional time part and zone suffix. */
const ISO_DATE_RE =
  /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/** `2026-06-06T00:00:00.000Z` → `2026-06-06`, keeping `HH:mm` only when the value
 *  actually carries a time. The date part is sliced from the string rather than
 *  parsed, so a UTC midnight never shifts to the previous day in a local zone. */
function formatDateLike(s: string): string | null {
  const m = ISO_DATE_RE.exec(s.trim());
  if (!m) return null;
  const [, date, hh, mm] = m;
  return hh && (hh !== "00" || mm !== "00") ? `${date} ${hh}:${mm}` : date;
}

/** Render any cell the SP hands back. Objects/arrays are JSON so nothing renders
 *  as "[object Object]"; null/undefined read as an em dash. */
function cellText(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v instanceof Date) return formatDateLike(v.toISOString()) ?? String(v);
  if (typeof v === "object") return JSON.stringify(v);
  if (typeof v === "string") return formatDateLike(v) ?? v;
  return String(v);
}

/** True when every present value in a column parses as a number — the only
 *  signal available for alignment, since the column names are unknown. */
function isNumericColumn(rows: MetricDrillDownRow[], col: string): boolean {
  let seenValue = false;
  for (const r of rows) {
    const v = r[col];
    if (v === null || v === undefined || v === "") continue;
    if (typeof v === "boolean" || typeof v === "object") return false;
    if (!Number.isFinite(Number(v))) return false;
    seenValue = true;
  }
  return seenValue;
}

export interface DrillDownScope {
  metricCode: string;
  metricName: string;
  /** Chips describing what the records are scoped to (period, service line, …). */
  scopeChips: string[];
}

/**
 * Drill Down modal for the All Metrics grid — the records behind one metric,
 * from `POST all-metric/drill-down` (`GetMetricDrillDown`).
 *
 * The table is fully dynamic: headers and cells come from whatever columns the SP
 * returns, so it serves every metric with `HasDrillDown = 1` and adapts to new
 * columns without a frontend change. Reused as-is by the By Month, By Week and
 * By Day views — only the `params` window differs.
 */
export default function MetricDrillDownModal({
  scope,
  params,
  onClose,
}: {
  scope: DrillDownScope;
  /** Null only while the service line is unresolved — the modal then just shows
   *  its empty state rather than fetching with a missing scope. */
  params: MetricDrillDownParams | null;
  onClose: () => void;
}) {
  const { rows, loading, error } = useMetricDrillDown(params);

  // Escape closes, matching the drill panel.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const columns = useMemo(() => columnsOf(rows), [rows]);
  // Derived from the FULL row set, not the filtered one, so a column doesn't
  // change alignment as the user types into it.
  const numericCols = useMemo(() => {
    const set = new Set<string>();
    for (const c of columns) if (isNumericColumn(rows, c)) set.add(c);
    return set;
  }, [columns, rows]);

  // Every cell's DISPLAY text, and a lowercased copy for matching, built once per
  // payload. `cellText` runs a regex per cell, and it used to run on every cell
  // twice per keystroke — once to filter and once to re-render — which is what
  // made typing lag on a large record set. Now the keystroke path only reads
  // prebuilt strings.
  const grid = useMemo(() => {
    const text = rows.map((r) => columns.map((c) => cellText(r[c])));
    return { text, lower: text.map((tr) => tr.map((t) => t.toLowerCase())) };
  }, [rows, columns]);

  // Per-column "contains" filters, keyed by column name. Client-side only: the
  // drill-down SP takes no such parameters, and the records are already in
  // memory, so narrowing them costs one pass and no round trip.
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const setColFilter = (col: string, v: string) =>
    setColFilters((f) => ({ ...f, [col]: v }));
  const clearFilters = () => setColFilters({});

  // The inputs render from `colFilters` (immediate) but the TABLE filters on the
  // deferred copy, so React paints the keystroke first and re-runs the rows at
  // low priority. Without this the input can't update until the whole tbody has
  // re-rendered — the "text appears late" symptom, which no amount of making the
  // filter pass itself faster would fix.
  const deferredFilters = useDeferredValue(colFilters);
  const filtering = deferredFilters !== colFilters;

  // Only the columns actually being filtered, as [columnIndex, lowercased query].
  // Reading them off `columns` drops filters left over from a previous,
  // differently-shaped result.
  const activeFilters = useMemo(() => {
    const out: [number, string][] = [];
    columns.forEach((c, ci) => {
      const q = (deferredFilters[c] ?? "").trim().toLowerCase();
      if (q) out.push([ci, q]);
    });
    return out;
  }, [columns, deferredFilters]);

  // Row indices into `grid`, or null for "everything" — so the unfiltered case
  // allocates nothing. Matching runs against the RENDERED text, so what the user
  // types is matched against what they can actually see: "2026-06" finds a date
  // the SP sent as a full ISO timestamp, and "yes" finds a boolean true.
  const visibleIdx = useMemo(() => {
    if (!activeFilters.length) return null;
    const out: number[] = [];
    for (let i = 0; i < grid.lower.length; i++) {
      const row = grid.lower[i];
      let ok = true;
      for (const [ci, q] of activeFilters) {
        if (!row[ci].includes(q)) { ok = false; break; }
      }
      if (ok) out.push(i);
    }
    return out;
  }, [grid, activeFilters]);

  const rowIdx = useMemo(
    () => visibleIdx ?? grid.text.map((_, i) => i),
    [visibleIdx, grid],
  );
  const visibleCount = visibleIdx ? visibleIdx.length : rows.length;

  const downloadCsv = () => {
    if (!columns.length) return;
    const lines: string[] = [];
    lines.push(csvEscape(`DayLight Drill Down — ${scope.metricName}`));
    lines.push(csvEscape(scope.scopeChips.join(" · ")));
    // The export follows the screen: filters narrow the CSV too, and the header
    // says so rather than leaving the user to wonder which rows they got.
    if (activeFilters.length)
      lines.push(
        csvEscape(
          `Filtered — ${activeFilters
            .map(([ci]) => `${columns[ci]}: ${(deferredFilters[columns[ci]] ?? "").trim()}`)
            .join(" · ")}`,
        ),
      );
    lines.push("");
    lines.push(columns.map(csvEscape).join(","));
    rowIdx.forEach((i) => lines.push(grid.text[i].map(csvEscape).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `DayLight_DrillDown_${scope.metricCode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="record-drill-scrim" onClick={onClose} />
      <div className="record-drill dl" role="dialog" aria-label={`Records for ${scope.metricName}`}>
        <div className="rd-head">
          <div className="rd-title-row">
            <div>
              <div className="rd-eyebrow">Records</div>
              <h2 className="rd-title">{scope.metricName}</h2>
            </div>
            <button className="rd-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
          </div>
          <div className="rd-scope">
            {scope.scopeChips.map((c, i) => (
              <span key={c} className={"rd-scope-chip" + (i === 0 ? " accent" : "")}>{c}</span>
            ))}
          </div>
        </div>

        <div className="rd-body">
          {loading ? (
            <DrillDownLoading metricName={scope.metricName} />
          ) : error ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>{error}</div>
          ) : !rows.length ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
              No records for this metric in the selected period.
            </div>
          ) : (
            <table className={"rd-table" + (filtering ? " filtering" : "")}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} className={numericCols.has(c) ? "num" : ""}>
                      <span className="rd-th-label">{c}</span>
                      <input
                        className={
                          "rd-colfilter" + (colFilters[c]?.trim() ? " on" : "")
                        }
                        value={colFilters[c] ?? ""}
                        onChange={(e) => setColFilter(c, e.target.value)}
                        placeholder="Filter"
                        aria-label={`Filter by ${c}`}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* The table stays mounted when a filter matches nothing — the
                    inputs are IN the header, so unmounting it would take away
                    the only way to undo the filter that emptied it. */}
                {!rowIdx.length ? (
                  <tr>
                    <td className="rd-nomatch" colSpan={columns.length}>
                      No records match these filters.
                      <button className="rd-filter-clear" onClick={clearFilters}>
                        Clear filters
                      </button>
                    </td>
                  </tr>
                ) : (
                  rowIdx.map((ri) => (
                    <tr key={ri}>
                      {grid.text[ri].map((t, ci) => (
                        <td key={columns[ci]} className={numericCols.has(columns[ci]) ? "num" : ""}>{t}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="rd-foot">
          <span className="rd-count">
            {loading
              ? "Loading records…"
              : activeFilters.length
                // Both numbers, so a filter that hides most of the set can never
                // be mistaken for the SP returning that little.
                ? `Showing ${visibleCount} of ${rows.length} record${rows.length === 1 ? "" : "s"}`
                : `Showing ${rows.length} record${rows.length === 1 ? "" : "s"}`}
            {activeFilters.length > 0 && (
              <button className="rd-filter-clear" onClick={clearFilters}>Clear filters</button>
            )}
          </span>
          <button className="dl-btn rd-download" onClick={downloadCsv} disabled={loading || !visibleCount}>
            <Download size={14} />Download CSV
          </button>
        </div>
      </div>
    </>
  );
}
